import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Stack,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Paper,
    InputAdornment
} from "@mui/material";

import LinkIcon from "@mui/icons-material/Link";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import KeyIcon from "@mui/icons-material/Key";
import ApartmentIcon from "@mui/icons-material/Apartment";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";

import Swal from "sweetalert2";
import { db, auth } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

const colors = {
    darkGreen: '#06362D',
    calPolyGreen: '#155332',
    vanilla: '#DEE2B1',
    gold: '#FFD500',
    goldenrod: '#D1A505'
};

function Cmconnection() {
    const [propertyId, setPropertyId] = useState("");
    const [authCode, setAuthCode] = useState("");
    const [hotelCode, setHotelCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [connected, setConnected] = useState(false);
    const [rooms, setRooms] = useState([]);

    const urls = {
        baseUrl: "https://pmsapi.channelmanager.com.au/api/cmpms",
        roomUrl: "https://pmsapi.channelmanager.com.au/api/cmpms/roominfo",
        inventoryUrl: "https://pmsapi.channelmanager.com.au/api/cmpms/UpdateInventory",
        rateUrl: "https://pmsapi.channelmanager.com.au/api/cmpms/rateupdat",
        restrictionUrl: "https://pmsapi.channelmanager.com.au/api/cmpms/UpdateRestriction"
    };

    /* FETCH PROPERTY ID */
    useEffect(() => {
        const fetchProperty = async () => {
            try {
                const uid = auth.currentUser?.uid;
                if (!uid) return;

                const docRef = doc(db, "authroles", uid);
                const snap = await getDoc(docRef);

                if (snap.exists()) {
                    setPropertyId(snap.data().propertyId);
                }
            } catch (error) {
                console.error("Error fetching property ID:", error);
            }
        };
        fetchProperty();
    }, []);

    /* FETCH CHANNEL MANAGER SETTINGS */
    useEffect(() => {
        const fetchChannelManager = async () => {
            if (!propertyId) return;
            try {
                const ref = doc(db, "channel_manager", propertyId);
                const snap = await getDoc(ref);

                if (snap.exists()) {
                    const data = snap.data();
                    setAuthCode(data.authCode || "");
                    setHotelCode(data.hotelCode || "");
                    setConnected(data.status === "connected");
                }
            } catch (error) {
                console.error("Error fetching channel manager settings:", error);
            }
        };
        fetchChannelManager();
    }, [propertyId]);

    /* SAVE SETTINGS */
    const saveConfig = async () => {
        if (!authCode || !hotelCode) {
            Swal.fire({ icon: "warning", title: "Missing Information", text: "Please enter both Hotel Code and AuthCode." });
            return;
        }
        if (!propertyId) {
            Swal.fire({ icon: "error", title: "Error", text: "Property ID not loaded yet. Please wait." });
            return;
        }

        try {
            setLoading(true);
            const ref = doc(db, "channel_manager", propertyId);

            await setDoc(ref, {
                propertyId,
                authCode,
                hotelCode,
                baseUrl: urls.baseUrl,
                roomUrl: urls.roomUrl,
                inventoryUrl: urls.inventoryUrl,
                rateUrl: urls.rateUrl,
                restrictionUrl: urls.restrictionUrl,
                status: "connected",
                updatedAt: serverTimestamp()
            }, { merge: true });

            setConnected(true);
            Swal.fire({ icon: "success", title: "Saved!", text: "Configuration connected and updated successfully." });
        } catch (error) {
            Swal.fire({ icon: "error", title: "Error Saving", text: error.message });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: colors.darkGreen, color: colors.vanilla, p: { xs: 2, md: 1 } }}>
            <Box component="main" sx={{ flexGrow: 1, maxWidth: "1400px", margin: "0 auto" }}>
                
                {/* HEADER SECTION */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4, flexWrap: "wrap", gap: 2 }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: colors.vanilla, letterSpacing: "-0.5px" }}>
                            Channel Manager
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.goldenrod, mt: 0.5, opacity: 0.9 }}>
                            Synchronize configurations, real-time rates, and room inventorial limits.
                        </Typography>
                    </Box>
                    <Chip
                        label={connected ? "Live & Connected" : "Action Required"}
                        sx={{ 
                            fontWeight: 600, 
                            px: 1, 
                            height: 32,
                            borderRadius: "8px",
                            backgroundColor: connected ? colors.calPolyGreen : colors.goldenrod,
                            color: colors.vanilla,
                            border: `1px solid ${colors.gold}`,
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)"
                        }}
                    />
                </Box>

                <div className="container-fluid p-0">
                    <div className="row">
                        {/* CONFIGURATION PANEL */}
                        <div className="col-sm-7 mb-4">
                            <Card sx={{ borderRadius: 4, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)", border: `1px solid ${colors.goldenrod}`, backgroundColor: colors.calPolyGreen, color: colors.vanilla, height: "100%" }}>
                                <CardContent sx={{ p: 4 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: colors.vanilla, mb: 1 }}>
                                        Connection Parameters
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: colors.goldenrod, mb: 3, opacity: 0.9 }}>
                                        Input your credentials acquired from your Channel Manager ecosystem vendor.
                                    </Typography>
                                    
                                    <Divider sx={{ mb: 4, borderColor: colors.goldenrod, opacity: 0.4 }} />

                                    <Stack spacing={3}>
                                        <TextField
                                            label="Hotel Code"
                                            variant="outlined"
                                            value={hotelCode}
                                            onChange={(e) => setHotelCode(e.target.value)}
                                            fullWidth
                                            InputLabelProps={{ style: { color: colors.vanilla } }}
                                            InputProps={{
                                                style: { color: colors.vanilla },
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <ApartmentIcon sx={{ color: colors.goldenrod }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{ 
                                                "& .MuiOutlinedInput-root": { 
                                                    borderRadius: 3,
                                                    "& fieldset": { borderColor: colors.goldenrod },
                                                    "&:hover fieldset": { borderColor: colors.gold },
                                                    "&.Mui-focused fieldset": { borderColor: colors.gold }
                                                } 
                                            }}
                                        />

                                        <TextField
                                            label="API Authentication Token"
                                            variant="outlined"
                                            value={authCode}
                                            onChange={(e) => setAuthCode(e.target.value)}
                                            fullWidth
                                            InputLabelProps={{ style: { color: colors.vanilla } }}
                                            InputProps={{
                                                style: { color: colors.vanilla },
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <KeyIcon sx={{ color: colors.goldenrod }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{ 
                                                "& .MuiOutlinedInput-root": { 
                                                    borderRadius: 3,
                                                    "& fieldset": { borderColor: colors.goldenrod },
                                                    "&:hover fieldset": { borderColor: colors.gold },
                                                    "&.Mui-focused fieldset": { borderColor: colors.gold }
                                                } 
                                            }}
                                        />

                                        <Box sx={{ display: "flex", gap: 2, pt: 1, flexWrap: "wrap" }}>
                                            <Button
                                                variant="contained"
                                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CloudDoneIcon />}
                                                onClick={saveConfig}
                                                disabled={loading}
                                                sx={{ 
                                                    borderRadius: 3, 
                                                    px: 4, 
                                                    py: 1.2, 
                                                    textTransform: "none", 
                                                    fontWeight: 600,
                                                    backgroundColor: colors.goldenrod,
                                                    color: colors.darkGreen,
                                                    "&:hover": { backgroundColor: colors.gold },
                                                    boxShadow: "0 4px 14px 0 rgba(209, 165, 5, 0.4)"
                                                }}
                                            >
                                                Save Settings
                                            </Button>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </div>

                        {/* INFORMATION HUB */}
                        <div className="col-sm-5 mb-4">
                            <Card sx={{ borderRadius: 4, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)", border: `1px solid ${colors.goldenrod}`, backgroundColor: colors.calPolyGreen, color: colors.vanilla, height: "100%" }}>
                                <CardContent sx={{ p: 4 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: colors.vanilla, mb: 1 }}>
                                        Endpoint Architecture
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: colors.goldenrod, mb: 3, opacity: 0.9 }}>
                                        System targeted distribution hooks routed transparently.
                                    </Typography>
                                    <Divider sx={{ mb: 3, borderColor: colors.goldenrod, opacity: 0.4 }} />

                                    <Stack spacing={2.5}>
                                        {[
                                            { label: "Base API Entry", value: urls.baseUrl },
                                            { label: "Room Mapping Route", value: urls.roomUrl },
                                            { label: "Inventory Handshake", value: urls.inventoryUrl }
                                        ].map((item, idx) => (
                                            <Box key={idx}>
                                                <Typography variant="caption" sx={{ fontWeight: 600, color: colors.goldenrod, textTransform: "uppercase" }}>
                                                    {item.label}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontFamily: "monospace", color: colors.vanilla, wordBreak: "break-all", mt: 0.5, bgcolor: colors.darkGreen, p: 1, borderRadius: 1.5, border: `1px solid ${colors.goldenrod}` }}>
                                                    {item.value}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>

                {/* ROOM MAPPING SCHEDULER TABLE */}
                {rooms.length > 0 && (
                    <Card sx={{ mt: 3, borderRadius: 4, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)", border: `1px solid ${colors.goldenrod}`, backgroundColor: colors.calPolyGreen, color: colors.vanilla, overflow: "hidden" }}>
                        <Box sx={{ p: 3, display: "flex", alignItems: "center", gap: 1.5, bgcolor: colors.darkGreen, borderBottom: `1px solid ${colors.goldenrod}` }}>
                            <MeetingRoomIcon sx={{ color: colors.gold }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.vanilla }}>
                                Active Rates & Structural Mapping
                            </Typography>
                        </Box>
                        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0, backgroundColor: 'transparent' }}>
                            <Table sx={{ minWidth: 650 }}>
                                <TableHead sx={{ backgroundColor: colors.darkGreen }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600, color: colors.vanilla }}>Index</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: colors.vanilla }}>Room Identifier</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: colors.vanilla }}>Room Classification</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: colors.vanilla }}>Rate Plan Token ID</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: colors.vanilla }}>Assigned Target Strategy</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rooms.flatMap((room, index) => {
                                        const ratePlans = room?.RoomRatePlans?.RoomRatePlanData || [];
                                        return ratePlans.map((plan, i) => (
                                            <TableRow 
                                                key={`${room.RoomId}-${i}`}
                                                sx={{ "&:hover": { backgroundColor: "rgba(255,255,255,0.05)" }, transition: "background-color 0.2s" }}
                                            >
                                                {i === 0 ? (
                                                    <>
                                                        <TableCell rowSpan={ratePlans.length} sx={{ fontWeight: 500, color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{index + 1}</TableCell>
                                                        <TableCell rowSpan={ratePlans.length} sx={{ fontFamily: "monospace", color: colors.gold, fontWeight: 600, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{room.RoomId}</TableCell>
                                                        <TableCell rowSpan={ratePlans.length} sx={{ fontWeight: 600, color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{room.RoomName}</TableCell>
                                                    </>
                                                ) : null}
                                                <TableCell sx={{ fontFamily: "monospace", color: colors.vanilla, opacity: 0.9, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{plan.RatePlanId}</TableCell>
                                                <TableCell sx={{ borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                                                    <Chip 
                                                        label={plan.RatePlanName} 
                                                        size="small" 
                                                        sx={{ bgcolor: colors.darkGreen, color: colors.vanilla, fontWeight: 500, border: `1px solid ${colors.goldenrod}` }} 
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ));
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                )}
            </Box>
        </Box>
    );
}

export default Cmconnection;